import {NexusSearchSchemaEntryNames, PlatformIndustryIds} from '../../constants/enums';
import EnvService from '../env';

export type IndustryFeatureRules = {
    vinDecoderEnabled?: boolean;
    /** Client-side Nexus result aggregation by variety (Amazon-style variance cards). */
    aggregatedVariantsNexusSearch?: boolean;
};

export type PartnerDefaultNexusSearchOrderBy = {
    schemaEntryName: string;
    isDescending: boolean;
};

/**
 * How lookup-v2 fetches live warehouse availability for loaded products.
 * - searchedAndMainResults: after a search, main/score-threshold products only
 * - searched: after a search, every loaded result
 * - always: every loaded product not already fetched, regardless of search or score
 */
export type LookupLiveAvailabilityMode = 'searchedAndMainResults' | 'searched' | 'always';

const DEFAULT_LOOKUP_LIVE_AVAILABILITY_MODE: LookupLiveAvailabilityMode = 'searchedAndMainResults';

export type PartnerFeatureRules = {
    /**When true, allows special characters in keywords / product codes (e.g. CHTP, Myers).*/
    allowKeywordSpecialCharacters?: boolean;
    /**When set, overrides the default Nexus Search orderBy for the partner.*/
    defaultNexusSearchOrderBy?: PartnerDefaultNexusSearchOrderBy;
    /** CHTP-style: product headers use secondary palette color. */
    preferSecondaryForProductHeaders?: boolean;
    /** CHTP: show QMS Panel in connected apps popover. */
    showQmsPanelApp?: boolean;
    /** When true, lookup does not split results into Main vs Other Relevant Products. */
    disableNexusMainOtherBucketing?: boolean;
    /**When true: treat customers as COD for payment gating (balance-only) and hide Apply Credit on quotes, invoices, and multi-invoice payments. Payment terms remain editable.*/
    forceCustomersAsCod?: boolean;
    /**
     * How lookup-v2 fetches live warehouse availability for loaded products.
     * Defaults to searchedAndMainResults when unset.
     */
    lookupLiveAvailabilityMode?: LookupLiveAvailabilityMode;
};

type OrderPaymentGateInput = {
    balance?: number | string | null;
    payInfo?: {
        isNeededToPay?: boolean;
        minimumRequiredPayment?: number | string | null;
        /** Quote/SO payment bypass — when true, payment is not required. */
        byPassed?: boolean;
    } | null;
} | null | undefined;

export type NexusSearchOrderBy = {
    id: string;
    isDescending: boolean;
};

type SchemaEntry = {
    id: string;
    name: string;
    sortable?: boolean;
};

const VIN_DECODER_INDUSTRY_IDS = [
    PlatformIndustryIds.hdAutoPart,
    PlatformIndustryIds.autoPart,
    PlatformIndustryIds.machinery,
] as const;

const VARIANCE_AGGREGATION_INDUSTRY_IDS = [
    PlatformIndustryIds.westernApparel,
] as const;

const buildIndustryRules = (): Record<string, IndustryFeatureRules> => {
    const rules: Record<string, IndustryFeatureRules> = {};

    for (const id of VIN_DECODER_INDUSTRY_IDS) {
        const key = id.toLowerCase();
        rules[key] = {
            ...rules[key],
            vinDecoderEnabled: true,
        };
    }

    for (const id of VARIANCE_AGGREGATION_INDUSTRY_IDS) {
        const key = id.toLowerCase();
        rules[key] = {
            ...rules[key],
            aggregatedVariantsNexusSearch: true,
        };
    }

    return rules;
};

const buildPartnerRules = (): Record<string, PartnerFeatureRules> => {
    const rules: Record<string, PartnerFeatureRules> = {};

    const horsebarn = EnvService.DefinedPartnerIds.horsebarn?.toUpperCase();
    if (horsebarn) {
        rules[horsebarn] = {
            ...rules[horsebarn],
            allowKeywordSpecialCharacters: true,
            disableNexusMainOtherBucketing: true,
            forceCustomersAsCod: true,
        };
    }

    const myers = EnvService.DefinedPartnerIds.myers?.toUpperCase();
    if (myers) {
        rules[myers] = {
            ...rules[myers],
            allowKeywordSpecialCharacters: true,
            lookupLiveAvailabilityMode: 'always',
            defaultNexusSearchOrderBy: {
                schemaEntryName: NexusSearchSchemaEntryNames.partNo,
                isDescending: false,
            },
        };
    }

    const chtp = EnvService.DefinedPartnerIds.chtp?.toUpperCase();
    if (chtp) {
        rules[chtp] = {
            ...rules[chtp],
            preferSecondaryForProductHeaders: true,
            showQmsPanelApp: true,
        };
    }

    const chtpEdmonton = EnvService.DefinedPartnerIds.chtpEdmonton?.toUpperCase();
    if (chtpEdmonton) {
        rules[chtpEdmonton] = {
            ...rules[chtpEdmonton],
            preferSecondaryForProductHeaders: true,
        };
    }

    return rules;
};

const INDUSTRY_FEATURE_RULES: Record<string, IndustryFeatureRules> = buildIndustryRules();
const PARTNER_FEATURE_RULES: Record<string, PartnerFeatureRules> = buildPartnerRules();

/**
 * Partner- and industry-scoped feature rules for the operator platform.
 */
class PartnerIndustryRules {

    /**
     * Parses an order money field to a finite number (invalid / empty → 0).
     */
    static parseOrderMoney = (value: number | string | null | undefined): number => {
        if (value == null || value === '') {
            return 0;
        }
        const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    /**
     * Returns industry feature rules for the given id.
     */
    static getIndustryRules(industryId?: string | number | null): IndustryFeatureRules {
        if (industryId == null || industryId === '') {
            return {};
        }
        return INDUSTRY_FEATURE_RULES[String(industryId).trim().toLowerCase()] ?? {};
    }

    /**
     * Returns partner feature rules for the given id.
     */
    static getPartnerRules(partnerId?: string | null): PartnerFeatureRules {
        if (!partnerId) {
            return {};
        }
        return PARTNER_FEATURE_RULES[partnerId.toUpperCase()] ?? {};
    }

    /**
     * Whether the industry supports VIN decoder.
     */
    static isVinDecoderIndustry(industry?: { id?: string | number } | null): boolean {
        return Boolean(PartnerIndustryRules.getIndustryRules(industry?.id).vinDecoderEnabled);
    }

    /**
     * Whether the industry supports Aggregated Variants Nexus Search.
     */
    static isVarianceAggregationIndustry(industry?: { id?: string | number } | null): boolean {
        return Boolean(
            PartnerIndustryRules.getIndustryRules(industry?.id).aggregatedVariantsNexusSearch,
        );
    }

    /**
     * Whether the partner allows special characters in keywords / product codes.
     */
    static allowsKeywordSpecialCharacters(partnerId?: string | null): boolean {
        return Boolean(PartnerIndustryRules.getPartnerRules(partnerId).allowKeywordSpecialCharacters);
    }

    /**
     * Whether the partner uses secondary palette for product headers (CHTP).
     * Defaults to true when partnerId is missing (legacy color-theme behavior).
     */
    static preferSecondaryForProductHeaders(partnerId?: string | null): boolean {
        if (!partnerId) {
            return true;
        }
        return Boolean(PartnerIndustryRules.getPartnerRules(partnerId).preferSecondaryForProductHeaders);
    }

    /**
     * Whether the partner should show QMS Panel in connected apps (CHTP).
     */
    static showQmsPanelApp(partnerId?: string | null): boolean {
        return Boolean(PartnerIndustryRules.getPartnerRules(partnerId).showQmsPanelApp);
    }

    /**
     * Whether the partner skips Main vs Other Relevant Products score bucketing in lookup.
     */
    static disablesNexusMainOtherBucketing(partnerId?: string | null): boolean {
        return Boolean(PartnerIndustryRules.getPartnerRules(partnerId).disableNexusMainOtherBucketing);
    }

    /**
     * How lookup-v2 fetches live warehouse availability for loaded products.
     * Defaults to searchedAndMainResults when the partner has no override.
     */
    static lookupLiveAvailabilityMode(partnerId?: string | null): LookupLiveAvailabilityMode {
        return PartnerIndustryRules.getPartnerRules(partnerId).lookupLiveAvailabilityMode
            ?? DEFAULT_LOOKUP_LIVE_AVAILABILITY_MODE;
    }

    /**
     * Whether the partner forces COD-style payment gating and hides Apply Credit flows.
     */
    static forcesCustomersAsCod(partnerId?: string | null): boolean {
        return Boolean(PartnerIndustryRules.getPartnerRules(partnerId).forceCustomersAsCod);
    }

    /**
     * Whether an order/invoice still needs payment for UI gating (quotes, sales orders, invoices).
     * When forceCustomersAsCod is on, only a positive balance matters (ignores server payInfo),
     * except bypassed orders (`payInfo.byPassed`) which never require payment.
     * Handles missing orders and non-numeric balances gracefully.
     */
    static effectiveIsNeededToPay(order: OrderPaymentGateInput, partnerId?: string | null): boolean {
        if (!order) {
            return false;
        }
        if (PartnerIndustryRules.forcesCustomersAsCod(partnerId)) {
            if (order.payInfo?.byPassed) {
                return false;
            }
            return PartnerIndustryRules.parseOrderMoney(order.balance) > 0;
        }
        return Boolean(order.payInfo?.isNeededToPay);
    }

    /**
     * Remaining amount to collect for pay dialogs / multi-invoice totals.
     * Works for quotes, sales orders, and invoices:
     * - forceCustomersAsCod: order balance (including bypassed orders for optional “pay anyways”)
     * - otherwise when payInfo.isNeededToPay: minimumRequiredPayment (fallback balance)
     * - otherwise: order balance (e.g. COD “add payment anyways”)
     */
    static effectiveRemainingBalance(order: OrderPaymentGateInput, partnerId?: string | null): number {
        if (!order) {
            return 0;
        }
        if (PartnerIndustryRules.forcesCustomersAsCod(partnerId)) {
            return PartnerIndustryRules.parseOrderMoney(order.balance);
        }
        if (order.payInfo?.isNeededToPay) {
            const minimumRequired = order.payInfo?.minimumRequiredPayment;
            if (minimumRequired != null && minimumRequired !== '') {
                return PartnerIndustryRules.parseOrderMoney(minimumRequired);
            }
        }
        return PartnerIndustryRules.parseOrderMoney(order.balance);
    }

    /**
     * Whether the order still has a positive balance that can optionally be paid
     * (e.g. after bypass under forceCustomersAsCod).
     */
    static hasOutstandingBalance(order: OrderPaymentGateInput): boolean {
        return PartnerIndustryRules.parseOrderMoney(order?.balance) > 0;
    }

    /**
     * Resolves partner default Nexus Search orderBy from schema entries.
     */
    static resolveDefaultNexusSearchOrderBy(
        partnerId: string | undefined | null,
        schemaEntries: SchemaEntry[] = [],
    ): NexusSearchOrderBy | null {
        if (!partnerId) {
            return null;
        }

        const config = PartnerIndustryRules.getPartnerRules(partnerId).defaultNexusSearchOrderBy;
        if (!config) {
            return null;
        }

        const entry = schemaEntries.find(
            (e) => e.name === config.schemaEntryName && e.sortable,
        );
        if (!entry) {
            return null;
        }

        return {
            id: entry.id,
            isDescending: config.isDescending,
        };
    }
}

export default PartnerIndustryRules;
